import isNil from 'lodash/isNil';

import { TANK_LEVEL_PRECISION } from 'constants/samplePoint';
import Sample from 'types/models/sample';
import { LevelDisplayUnit } from 'types/models/samplePoint';
import { DataType } from 'types/sample.enum';
import { convertMetricToImperialAlwaysRounding } from 'utils/convert-metric-to-imperial';
import { Reading } from 'utils/convert-unit';
import { UnitLevelSm, UnitType, getUnitSymbolByCountry } from 'utils/get-unit-by-country';
import { millisecondsToSeconds } from 'utils/unit-conversions';

/**
 * Forecasts a tank's liquid level at a given future time based on a past sample's date, value, and gradient.
 *
 * @param baseSample - The past sample containing the liquid level (in cm), the timestamp,
 * and the rate of change (per minute).
 * @param forecastDateInSeconds - The future date (in seconds) for which the forecast is to be made.
 * @param tankHeightMetric - The maximum height of the tank in the metric unit system (optional).
 * @returns The forecasted liquid level in centimetres, constrained by the tank height if provided.
 */
function getTankLevelForecast(
  baseSample: Sample,
  forecastDateInSeconds: number,
  tankHeightMetric?: number
): number | undefined {
  if (baseSample.dataType !== DataType.FORECAST) return undefined;

  const changePerMinute = baseSample.extraValues?.gradient;
  if (isNil(changePerMinute)) return undefined;

  if (changePerMinute === 0) {
    return baseSample.rwValue;
  }

  const minutesUntilForecast: number = (forecastDateInSeconds - millisecondsToSeconds(baseSample.date)) / 60;
  const forecastedValueMetric: number = baseSample.rwValue + minutesUntilForecast * changePerMinute;

  if (tankHeightMetric && forecastedValueMetric > tankHeightMetric) {
    return tankHeightMetric;
  }
  return Math.max(forecastedValueMetric, 0);
}

/**
 * Forecasts a tank's liquid level at a given future time and returns it with both metric and locale units.
 *
 * @param baseSample - The past sample containing the liquid level (in cm), the timestamp,
 * and the rate of change (per minute).
 * @param forecastDateInSeconds - The future date (in seconds) for which the forecast is to be made.
 * @param tankHeightMetric - The maximum height of the tank in the metric unit system (optional).
 * @param country - The country code to determine the unit system (default is 'AUS').
 * @returns The forecasted liquid level with both metric and locale units, constrained by the tank height if provided.
 */
export function getTankLevelForecastReading(
  baseSample: Sample,
  forecastDateInSeconds: number,
  tankHeightMetric?: number,
  country = 'AUS'
): Reading | undefined {
  const result = getTankLevelForecast(baseSample, forecastDateInSeconds, tankHeightMetric);
  if (result === undefined) return undefined;

  const unitSymbolLocale = getUnitSymbolByCountry<UnitLevelSm>({ unitType: UnitType.LEVEL_SM, country });
  return {
    metric: { value: result, unit: { symbol: LevelDisplayUnit.CM } },
    locale: {
      value: convertMetricToImperialAlwaysRounding(
        UnitType.LEVEL_SM,
        country,
        result,
        TANK_LEVEL_PRECISION
      )!, // Won't be undefined because result is not undefined
      unit: { symbol: unitSymbolLocale }
    }
  };
}